import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { GitCompareIcon } from "lucide-react";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface SelectableCardProps {
  /** Main title shown on the left side of the header row */
  title: ReactNode;
  /** Optional subtitle rendered beneath the title (e.g. contract period) */
  subtitle?: ReactNode;
  /** Optional badge rendered on the right side of the header row */
  badge?: ReactNode;
  /** KPI/body content rendered below the header row */
  children: ReactNode;
  /** Whether this card is currently the selected subject */
  isSelected: boolean;
  /** Whether this card is currently the comparing subject */
  isComparing: boolean;
  /** Whether any subject has been selected (controls whether compare CTA should show) */
  hasPrimarySelection: boolean;
  /** Fired when the card is clicked */
  onSelect: () => void;
  /** Fired when the compare button is clicked. If omitted, compare CTA will be hidden. */
  onToggleCompare?: (_e: React.MouseEvent) => void;
  /** Optional className to apply to the card */
  className?: string;
}

/**
 * SelectableCard - A reusable, clickable list card used in CalculationOverview.
 * Supports selected/comparing states and an optional compare CTA.
 *
 * @component
 * @example
 * <SelectableCard
 *   title="Service A"
 *   badge={<Badge>Permanent</Badge>}
 *   isSelected={false}
 *   isComparing={false}
 *   hasPrimarySelection={true}
 *   onSelect={() => setSelectedSubject(subject)}
 *   onToggleCompare={(e) => onCompareButtonClickHandler(e, subject)}
 * >
 *   <div className="flex flex-wrap gap-2">...</div>
 * </SelectableCard>
 */
function SelectableCard({
  title,
  subtitle,
  badge,
  children,
  isSelected,
  isComparing,
  hasPrimarySelection,
  onSelect,
  onToggleCompare,
  className,
}: SelectableCardProps) {
  const { t } = useTranslation();

  const showCompareCta = Boolean(onToggleCompare) && hasPrimarySelection && !isSelected;

  return (
    <Card
      className={cn(
        "p-4 hover:bg-slate-50 cursor-pointer",
        className,
        isSelected && "bg-blue-50 border-blue-800 hover:bg-blue-50",
        isComparing && "bg-orange-50 border-orange-800 hover:bg-orange-50"
      )}
      onClick={onSelect}
    >
      <div className="flex flex-col gap-2 w-full mr-2 text-start">
        <div className="flex items-start">
          <div className="grow flex flex-col gap-1">
            <span className="break-all">{title}</span>
            {subtitle ? <div className="text-sm text-muted-foreground">{subtitle}</div> : null}
          </div>
          <div className="grow"></div>
          <div className="flex items-center gap-x-2">
            {badge}
            {!hasPrimarySelection ? null : isSelected ? (
              <div className="text-xs text-blue-500">{t("edittender.calculationoverview.common.selected")}</div>
            ) : showCompareCta ? (
              <Button
                variant="outline"
                size="sm"
                className={cn(
                  "h-6 px-2 hover:bg-orange-50 rounded-sm hover:text-orange-400",
                  isComparing && "text-orange-500"
                )}
                title={t("edittender.calculationoverview.common.compare")}
                onClick={onToggleCompare}
              >
                <GitCompareIcon className="w-4 h-4" />
                <span className="text-xs">
                  {isComparing ? t("common:buttons.cancel") : t("edittender.calculationoverview.common.compare")}
                </span>
              </Button>
            ) : null}
          </div>
        </div>

        <div>{children}</div>
      </div>
    </Card>
  );
}

export default SelectableCard;
